import React from 'react';
import { motion } from 'framer-motion';
import { FileStack, FolderOutput, ScanText, Stamp, FileType2, Check } from 'lucide-react';
import { BorderBeam } from './magicui/border-beam';

// Illustrative PDF Flows pipeline: a batch moving through chained steps.
const steps = [
  { icon: ScanText, label: 'Recognise text', meta: 'OCR · offline' },
  { icon: FileType2, label: 'Convert', meta: 'PDF → DOCX' },
  { icon: Stamp, label: 'Watermark', meta: 'Text + date' },
];

export default function FlowCard({ className = '' }) {
  return (
    <div className={`relative overflow-hidden rounded-3xl bg-white p-5 shadow-lift sm:p-6 ${className}`}>
      <div className="flex items-center justify-between border-b border-line pb-4">
        <div className="flex items-center gap-2">
          <span className="size-2.5 rounded-full bg-[#FF5F57]" />
          <span className="size-2.5 rounded-full bg-[#FEBC2E]" />
          <span className="size-2.5 rounded-full bg-[#28C840]" />
        </div>
        <span className="font-mono text-[0.68rem] uppercase tracking-[0.16em] text-ink-3">PDF Flows · Scans to archive</span>
      </div>

      <div className="mt-5 flex items-center gap-3 rounded-2xl bg-mist p-3.5">
        <span className="inline-flex size-10 items-center justify-center rounded-xl bg-white text-fusion-blue shadow-card">
          <FileStack className="size-5" aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-semibold">Input folder</p>
          <p className="truncate font-mono text-xs text-ink-3">Scans / Incoming</p>
        </div>
      </div>

      <ol className="relative mt-3 space-y-3 pl-5 before:absolute before:bottom-6 before:left-[1.62rem] before:top-0 before:w-px before:bg-gradient-to-b before:from-fusion-blue before:to-fusion-teal">
        {steps.map(({ icon: Icon, label, meta }, i) => (
          <motion.li
            key={label}
            initial={{ opacity: 0, x: -8 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 + i * 0.15, duration: 0.4 }}
            className="relative flex items-center gap-3 rounded-2xl border border-line bg-white p-3"
          >
            <span className="inline-flex size-8 items-center justify-center rounded-lg bg-ink text-white">
              <Icon className="size-4" aria-hidden="true" />
            </span>
            <span className="flex-1 text-sm font-medium">{label}</span>
            <span className="font-mono text-[0.7rem] text-ink-3">{meta}</span>
            <Check className="size-4 text-fusion-teal" aria-hidden="true" />
          </motion.li>
        ))}
      </ol>

      <div className="mt-3 flex items-center gap-3 rounded-2xl bg-ink p-3.5 text-white">
        <span className="inline-flex size-10 items-center justify-center rounded-xl bg-white/10">
          <FolderOutput className="size-5" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">Archive</p>
          <p className="truncate font-mono text-xs text-white/60">Named, sorted, sealed</p>
        </div>
        <span className="rounded-full bg-fusion-teal/20 px-2.5 py-1 font-mono text-[0.68rem] text-fusion-teal">On this PC</span>
      </div>

      <BorderBeam size={120} duration={8} colorFrom="#3355FF" colorTo="#14B8A6" borderWidth={1.5} />
    </div>
  );
}
